import { teamPageMemberSchema } from "./team-members";
import { z } from "zod";
import { publicServiceSchema } from "./services";
import { serviceCategorySchema } from "./service-categories";

export const salonImageSchema = z.object({
  id: z.string().nullable().optional(),
  url: z.string().nullable().optional(),
});

export const salonBannerSchema = salonImageSchema.extend({
  type: z.enum(["image", "video"]).default("image"),
});

export const salonSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string().nullable().optional(),
  domain: z.string().nullable(),
  phoneNumber: z.string().nullable().optional(),
  /** Phone-assistant add-on number, if the business has the active add-on. */
  assistantPhoneNumber: z.string().nullable().optional(),
  supportEmail: z.string().nullable().optional(),
  timezone: z.string().nullable().optional(),
  logo: salonImageSchema.nullable().optional(),
  banner: salonBannerSchema.nullable().optional(),
  totalLocations: z.number().optional().default(0),
  ownerId: z.string(),
  cancellationPolicyEnabled: z.boolean().optional().default(true),
  cancellationPolicyHours: z.number().int().min(0).default(6),
  cancellationPolicyNote: z.string().nullable().optional(),
  lateGraceMinutes: z.number().int().min(0).max(60).default(10),
  /**
   * How many days ahead customers may book; null means no limit. The last
   * bookable date is today + N in the location's timezone. The API enforces
   * it; clients use it to stop the date picker and explain why.
   */
  customerBookingHorizonDays: z.number().int().positive().nullable().optional(),
  seoTitle: z.string().nullable().optional(),
  seoDescription: z.string().nullable().optional(),
  seoImageUrl: z.string().nullable().optional(),
  faviconUrl: z.string().nullable().optional(),
  /**
   * The booking page look the owner chose: a skin key from
   * `BOOKING_SKIN_KEYS` (`@openings/booking-core`), or null for the default
   * (Micro). Deliberately a plain string: a client that does not know a
   * newer skin renders Micro instead of failing to parse the salon.
   */
  bookingSkin: z.string().nullable().optional(),
  gaMeasurementId: z.string().nullable().optional(),
  googleAdsConversionId: z.string().nullable().optional(),
  googleAdsBookingConversionLabel: z.string().nullable().optional(),
  requireAnalyticsConsent: z.boolean().optional().default(false),
  defaultAdvertisingConsent: z.boolean().optional().default(false),
  instagramHandle: z.string().nullable().optional(),
  country: z.string().nullable().optional(),
  currency: z.string().nullable().optional(),
  verificationMethod: z.enum(["SMS", "EMAIL"]).optional().default("SMS"),
  /** Shows the blog on the booking page (Info row, `/blog`, sitemap). */
  blogEnabled: z.boolean().optional().default(false),
});

export const salonIdParamsSchema = z.object({
  id: z.string().min(1),
});

// ---------------------------------------------------------------------------
// Reserved slugs / platform subdomains
// ---------------------------------------------------------------------------

// These values are reserved by the platform (e.g. platform-only subdomains like
// manage.openings.link) and must never be claimable as a business slug.
export const reservedSalonSlugs = [
  "manage",
  "share",
  "booking",
  "website",
  "find",
  "app",
  "staff",
  "api",
  "docs",
  "support",
  "cdn",
  "embed",
  "www",
] as const;

export type ReservedSalonSlug = (typeof reservedSalonSlugs)[number];

export function normalizeSalonSlug(value: string): string {
  return value.trim().toLowerCase();
}

export function isReservedSalonSlug(value: string): boolean {
  const normalized = normalizeSalonSlug(value);
  return (reservedSalonSlugs as readonly string[]).includes(normalized);
}

export const salonSlugQuerySchema = z.object({
  slug: z.string().min(1),
});

export const salonDomainQuerySchema = z.object({
  domain: z.string().min(1),
});

export const salonSlugCheckQuerySchema = z.object({
  slug: z.string().min(1),
  excludeId: z.string().optional(),
});

export const slugAvailabilityResponseSchema = z.object({
  available: z.boolean(),
});

export type SalonImage = z.infer<typeof salonImageSchema>;
export type SalonBanner = z.infer<typeof salonBannerSchema>;
export type Salon = z.infer<typeof salonSchema>;
export type SalonIdParams = z.infer<typeof salonIdParamsSchema>;
export type SalonSlugQuery = z.infer<typeof salonSlugQuerySchema>;
export type SalonDomainQuery = z.infer<typeof salonDomainQuerySchema>;
export type SalonSlugCheckQuery = z.infer<typeof salonSlugCheckQuerySchema>;
export type SlugAvailabilityResponse = z.infer<
  typeof slugAvailabilityResponseSchema
>;

// ---------------------------------------------------------------------------
// Booking preview — GET /v1/salons/:id/booking-preview
// The data the standalone booking page and the feedback page need. It was the
// `bookingPreview` block of the template preview until the Oct 2026 template
// removal.
// ---------------------------------------------------------------------------

const bookingPreviewMemberServiceSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().nullable().optional(),
  price: z.number().optional(),
  duration: z.number().optional(),
  referenceId: z.string().nullable().optional(),
});

const bookingPreviewMemberSchema = z.object({
  id: z.string(),
  name: z.string(),
  photo: z.string().nullable().optional(),
  scheduleId: z.string().nullable().optional(),
  services: z.array(bookingPreviewMemberServiceSchema),
});

/** @deprecated Locations are merged into schedules */
const bookingPreviewLocationSchema = z.object({
  id: z.string(),
  name: z.string(),
  address: z.string(),
  images: z.array(z.string()).optional().default([]),
  lat: z.number().nullable().optional(),
  lng: z.number().nullable().optional(),
  parking: z
    .object({
      link: z.string().optional(),
      description: z.string().optional(),
    })
    .nullable()
    .optional(),
});

const bookingPreviewScheduleSchema = z.object({
  id: z.string(),
  title: z.string(),
  slug: z.string().optional(),
  address: z.string().nullable().optional(),
  image: z.string().optional(),
  /**
   * The wall-clock zone the API books this location in
   * (`resolveScheduleTimezone`), so the booking window's last day matches.
   */
  timezone: z.string().optional(),
  parking: z
    .object({
      link: z.string().optional(),
      description: z.string().optional(),
    })
    .nullable()
    .optional(),
  phone: z.string().nullable().optional(),
  instagramHandle: z.string().nullable().optional(),
});

export const bookingPreviewSchema = z.object({
  salonId: z.string(),
  /** Reuses publicServiceSchema, so a booking-preview service is a ServiceDTO. */
  services: z.array(publicServiceSchema),
  schedules: z.array(bookingPreviewScheduleSchema).optional().default([]),
  members: z.array(bookingPreviewMemberSchema),
  teamPageMembers: z.array(teamPageMemberSchema).optional(),
  scheduleAssignedServiceIds: z
    .record(z.string(), z.array(z.string()))
    .optional()
    .default({}),
  cancellationPolicyEnabled: z.boolean().optional().default(true),
  cancellationPolicyHours: z.number().nullable().optional(),
  serviceCategories: z.array(serviceCategorySchema).optional().default([]),
  /** @deprecated Use schedules */
  locations: z.array(bookingPreviewLocationSchema).optional().default([]),
  /** @deprecated Use scheduleAssignedServiceIds */
  locationAssignedServiceIds: z
    .record(z.string(), z.array(z.string()))
    .optional()
    .default({}),
});

export const bookingPreviewResponseSchema = z.object({
  bookingPreview: bookingPreviewSchema,
});

export type BookingPreview = z.infer<typeof bookingPreviewSchema>;
export type BookingPreviewMember = z.infer<typeof bookingPreviewMemberSchema>;
export type BookingPreviewSchedule = z.infer<
  typeof bookingPreviewScheduleSchema
>;
export type BookingPreviewResponse = z.infer<
  typeof bookingPreviewResponseSchema
>;
